import { FolderIcon } from '@phosphor-icons/react'
import * as Dialog from '@radix-ui/react-dialog'
import classNames from 'classnames'
import { useMemo, useState } from 'react'
import { useShowMutationError } from '../../hooks/use-show-mutation-error.tsx'
import { useToastService } from '../../services/toast-service.tsx'
import { useGetProjects, usePutRepositoryProjectMembership } from '../../utils/api-client/api-hooks.ts'
import type { ProjectResponse, Repository } from '../../utils/api-client/api-schemas.ts'
import { pluralize } from '../../utils/text-utils.ts'
import { DefaultButton } from '../buttons/default-button.tsx'
import { GhostButton } from '../buttons/ghost-button.tsx'
import { SearchInput } from '../input/search-input.tsx'
import { ADMIN_REQUIRED_OVERRIDE } from './admin-required-override.ts'
import * as styles from './project-dialog.css.ts'
import * as cardStyles from './project-row-card.css.ts'

export type MoveRepositoryDialogProps = {
  repository: Pick<Repository, 'id' | 'full_name'>
  /** The project the repository is leaving. */
  currentProject: Pick<ProjectResponse, 'id' | 'name'>
  onClose: () => unknown
  onMoved: (destination: ProjectResponse) => unknown
}

/** Picks the project a repository moves to. A repository belongs to one project, so this is a move. */
export const MoveRepositoryDialog: React.FC<MoveRepositoryDialogProps> = ({
  repository,
  currentProject,
  onClose,
  onMoved,
}) => {
  const { addToast } = useToastService()
  const [destinationId, setDestinationId] = useState<string>()
  const [search, setSearch] = useState('')
  const { projects } = useGetProjects()
  const allDestinations = useMemo(
    () => projects.filter(project => project.id !== currentProject.id),
    [projects, currentProject.id]
  )
  // An organization can have hundreds of projects, so the radio list is searchable by name. The
  // chosen destination is looked up in the unfiltered list: narrowing the view must not un-choose it.
  const destinations = useMemo(() => {
    const query = search.trim().toLowerCase()
    return query === ''
      ? allDestinations
      : allDestinations.filter(project => project.name.toLowerCase().includes(query))
  }, [allDestinations, search])
  const destination = allDestinations.find(project => project.id === destinationId)

  const showError = useShowMutationError({
    prefix: 'Failed to move repository',
    overrides: ADMIN_REQUIRED_OVERRIDE,
  })
  const moveMutation = usePutRepositoryProjectMembership({
    onSuccess: () => {
      if (destination) {
        addToast({
          message: (
            <>
              {repository.full_name} moved to &ldquo;{destination.name}&rdquo;
            </>
          ),
          variant: 'success',
        })
        onMoved(destination)
      }
    },
    onError: showError,
  })
  const isPending = moveMutation.isPending

  return (
    <Dialog.Root
      open
      onOpenChange={open => {
        if (!open && !isPending) {
          onClose()
        }
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className={styles.overlay} />
        <Dialog.Content className={styles.content} onPointerDownOutside={event => event.preventDefault()}>
          <Dialog.Title className={styles.title}>Move {repository.full_name}</Dialog.Title>
          <Dialog.Description asChild>
            <p className={styles.help}>
              A repository belongs to one project, so moving it here takes it out of {currentProject.name}.
            </p>
          </Dialog.Description>

          <SearchInput value={search} onChange={setSearch} size="small" name="Search projects" />

          <div className={styles.scrollList} role="radiogroup" aria-label="Destination project">
            {destinations.map((project, index) => {
              const isSelected = project.id === destinationId
              // One tab stop for the group; the arrows move within it, as a radio group does.
              const isTabStop = destination === undefined ? index === 0 : isSelected
              return (
                <div
                  key={project.id}
                  className={classNames(cardStyles.cardPick, cardStyles.cardOnDialog, {
                    [cardStyles.cardSelected]: isSelected,
                  })}
                  role="radio"
                  aria-checked={isSelected}
                  tabIndex={isTabStop ? 0 : -1}
                  onClick={() => setDestinationId(project.id)}
                  onKeyDown={event => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault()
                      setDestinationId(project.id)
                      return
                    }
                    const step =
                      event.key === 'ArrowDown' || event.key === 'ArrowRight'
                        ? 1
                        : event.key === 'ArrowUp' || event.key === 'ArrowLeft'
                          ? -1
                          : 0
                    if (step === 0) {
                      return
                    }
                    event.preventDefault()
                    const next = destinations[(index + step + destinations.length) % destinations.length]
                    if (next) {
                      setDestinationId(next.id)
                      const sibling = event.currentTarget.parentElement?.children[destinations.indexOf(next)]
                      if (sibling instanceof HTMLElement) {
                        sibling.focus()
                      }
                    }
                  }}
                >
                  <span className={cardStyles.badge}>
                    <FolderIcon size={20} weight="light" aria-hidden />
                  </span>
                  <span className={cardStyles.meta}>
                    <p className={cardStyles.name}>{project.name}</p>
                    <p className={cardStyles.sub}>
                      <span>
                        {project.repository_count} {pluralize(project.repository_count, 'repository', 'repositories')}
                      </span>
                      {project.description && (
                        <>
                          <i className={cardStyles.subSeparator}>·</i>
                          <span>{project.description}</span>
                        </>
                      )}
                    </p>
                  </span>
                  <span className={cardStyles.radio} aria-hidden />
                </div>
              )
            })}
            {destinations.length === 0 &&
              (search.trim() === '' ? (
                <p className={styles.emptyNote}>There is no other project to move it to.</p>
              ) : (
                <p className={styles.emptyNote}>No projects match &ldquo;{search.trim()}&rdquo;.</p>
              ))}
          </div>

          <div className={styles.barEnd}>
            <GhostButton
              type="secondary"
              className={styles.outlinedGhost}
              onClick={onClose}
              state={isPending ? 'disabled' : 'default'}
            >
              Cancel
            </GhostButton>
            <DefaultButton
              state={isPending ? 'loading' : destination === undefined ? 'disabled' : 'default'}
              onClick={() => {
                if (destination) {
                  moveMutation.mutate({ repositoryId: repository.id, projectId: destination.id })
                }
              }}
              loadingLabel="Moving repository"
            >
              Move repository
            </DefaultButton>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
